import type { DialogProps } from '@ariakit/react';
import type { FC, ReactElement, ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import { Button } from 'src/components/buttons-and-actions/button';
import type { StrictOmit } from 'src/utils/utility-types';

type ModalProps = DialogProps & {
  close: () => void;
};

/**
 * @deprecated Please use `useModal2` instead.
 */
export function useModal<T>(
  Component: FC<T & ModalProps>,
  props: StrictOmit<T & ModalProps, 'close'>
) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const modalProps = { ...props, close } as T & ModalProps;

  return {
    modal: isOpen ? <Component {...modalProps} /> : null,
    open,
    close,
  };
}

export type ModalApi = {
  close: () => void;
  closeButton: ReactNode;
};

type TriggerProps = {
  onClick: () => void;
};

type ModalProps2 = {
  modal: (props: ModalApi) => ReactNode;
  trigger: (props: TriggerProps) => ReactNode;
};

export function useModal2<T extends ModalProps2 | null>(
  props: T
): T extends null ? null : ModalHookReturn {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  if (props === null) return null as ModalHookOptionalReturn<T>;

  return {
    modal: isOpen
      ? props.modal({
          close,
          closeButton: (
            <Button mode="secondary" data-testid="close-modal" onClick={close}>
              Close
            </Button>
          ),
        })
      : null,
    open,
    close,
    trigger: props.trigger({ onClick: open }),
  } as ModalHookOptionalReturn<T>;
}

type ModalHookOptionalReturn<T> = T extends null ? null : ModalHookReturn;

type ModalHookReturn = {
  modal: ReactElement;
  open: () => void;
  close: () => void;
  trigger: ReactElement;
};

// Modal Context Provider
type ModalContextValue = {
  close: (() => void) | undefined;
};

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

type ModalContextProviderProps = {
  value: ModalContextValue;
  children: ReactNode;
};

export function ModalContextProvider({
  value,
  children,
}: ModalContextProviderProps) {
  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

export function useModalContext() {
  return useContext(ModalContext);
}
